/**
 * Aircraft picker — arms a type for click-to-place.
 *
 * Hovering a row warms the model cache so the first placement doesn't stall on
 * a network fetch.
 */
import { useMemo, useState } from 'react'
import { Plane, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAircraftLoaderInstance } from '@/services/3d'
import { useStudioStore } from '../store/studioStore'
import type { StudioCatalog } from '../data/loadStudioCatalog'
import { FIELD, PANEL, SECTION_TITLE } from './studioStyles'

interface Props {
  catalog: StudioCatalog | undefined
  isLoading: boolean
  onClose: () => void
}

export function AircraftLibraryPanel({ catalog, isLoading, onClose }: Props) {
  const [query, setQuery] = useState('')
  const placingTypeCode = useStudioStore((s) => s.placingTypeCode)
  const armPlacement = useStudioStore((s) => s.armPlacement)

  const entries = useMemo(() => {
    if (!catalog) return []
    const all = [...catalog.values()]
    const q = query.trim().toLowerCase()
    const filtered = q
      ? all.filter(
          (e) =>
            e.label.toLowerCase().includes(q) ||
            e.typeCode.toLowerCase().includes(q) ||
            (e.category ?? '').toLowerCase().includes(q)
        )
      : all
    return filtered.slice(0, 300)
  }, [catalog, query])

  return (
    <div className={cn(PANEL, 'flex max-h-[70vh] w-72 flex-col')}>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <Plane className="h-4 w-4 text-sky-300" />
        <span className={SECTION_TITLE}>Aircraft</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"
          title="Close"
        >
          <span className="text-base leading-none">×</span>
        </button>
      </div>

      <div className="relative px-3 py-2">
        <Search className="pointer-events-none absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search type…"
          className={cn(FIELD, 'pl-7')}
        />
      </div>

      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 pb-2">
        {isLoading && <p className="py-6 text-center text-[11px] text-white/40">Loading catalog…</p>}

        {!isLoading && entries.length === 0 && (
          <p className="py-6 text-center text-[11px] text-white/40">No matching types.</p>
        )}

        {entries.map((entry) => {
          const armed = placingTypeCode === entry.typeCode
          return (
            <button
              key={entry.typeCode}
              type="button"
              onMouseEnter={() => {
                const url = entry.aircraftType.modelSettings.filePath
                if (url) void getAircraftLoaderInstance().preloadModel(entry.typeCode, url)
              }}
              onClick={() => armPlacement(armed ? null : entry.typeCode)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left transition-colors',
                armed
                  ? 'border-sky-400/40 bg-sky-500/20'
                  : 'border-white/10 bg-white/5 hover:bg-white/10'
              )}
            >
              {entry.thumbnailUrl ? (
                <img
                  src={entry.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="h-8 w-10 shrink-0 rounded object-contain"
                />
              ) : (
                <div className="flex h-8 w-10 shrink-0 items-center justify-center rounded bg-white/5">
                  <Plane className="h-3.5 w-3.5 text-white/25" />
                </div>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[11px] font-medium text-white/85">
                  {entry.label}
                </span>
                <span className="block truncate text-[10px] text-white/35">
                  {entry.typeCode}
                  {!entry.hasModel && ' · no 3D model'}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <p className="border-t border-white/10 px-3 py-2 text-[10px] leading-relaxed text-white/35">
        {placingTypeCode
          ? 'Click the map to place. Esc to cancel.'
          : 'Pick a type, then click the map to place it.'}
      </p>
    </div>
  )
}
